/** @jsxRuntime classic */ // <-- remove this when TS updated to 4.1
/** @jsx jsx */ // <-- remove this when TS updated to 4.1
import { jsx } from '@emotion/core';
import { FC, Fragment, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useTheme } from 'emotion-theming';
import {
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Button,
  Form,
  Input,
  Page,
  Text,
  VoucherInfo,
} from '../..';
import { pricePresenter, textReplacer } from '../../../utils';
import { appStore } from '../../../state';
import { Copy, Discounts, Plan, ROUTES, ThemeProps, TrackingAction, TrackingPage, TrackingProperty, TrackingPropertyMap } from '../../../types';
import { useGetDiscountedPlans, useGetPlans } from '../../../hooks';
import { Mixpanel } from 'mixpanel-browser';
import getEventName from '../../../utils/getMixpanelEventName';
import getPlanDuration from '../../../utils/getPlanDuration';
interface PlanSelectorProps extends Copy {
  mixpanel: Mixpanel;
}

const PlanSelector: FC<PlanSelectorProps> = ({ fallbackCopy, localeCopy, mixpanel }) => {
  const history = useHistory();
  const theme: ThemeProps = useTheme();
  const { dispatch, state } = useContext(appStore);
  const { form, plans, skipPayment, config, params } = state;
  const { voucher } = params;
  const { currencySymbols, blacklistedPlans } = config;

  // initialise form
  const {
    clearErrors,
    control,
    errors,
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useForm({
    mode: 'all',
  });

  /**
   * Fetch plans on init
   */
  const plansQuery = useGetPlans({
    dispatch,
    errorText:
      localeCopy?.errors?.GENERIC_ERROR || fallbackCopy.errors.GENERIC_ERROR,
    reset,
    plans,
    blacklistedPlans
  });

  /**
   * Get discounted plan with voucher code as url param
   */
  const submittedVoucherCode = voucher ? voucher : watch('voucherCode');
  const {
    isLoading: disPlanIsLoading,
    refetch: getDisPlan,
  } = useGetDiscountedPlans({
    config: {
      enabled: false,
      onError: (error: any) => {
        console.error('Error with voucher request: ', error);
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            dismissable: true,
            id: new Date().getTime(),
            text:
              localeCopy?.errors?.INVALID_VOUCHER_CODE ||
              fallbackCopy.errors.INVALID_VOUCHER_CODE,
            timeout: 7,
            type: 'error',
          },
        });
      },
      onSuccess: (data: Plan[]) => {
        /**
         * Save voucher code to state
         */
        dispatch({
          type: 'UPDATE_FORM_DATA',
          payload: {
            voucherCode: submittedVoucherCode,
            selectedPlan: data[0],
          },
        });

        const discounts: Discounts = data[0].discounts[0];
        const voucherOnlyValidWithPayment = checkVoucherValidForPayment(discounts);

        setValue('selectedPlan', data[0], { shouldDirty: true });

        if (voucherOnlyValidWithPayment) {
          dispatch({
            type: 'UPDATE_PLANS',
            payload: data[0],
          });
        } else {
          dispatch({
            type: 'UPDATE_SKIP_PAYMENT',
            payload: {
              skip: true,
              planId: data[0]?.productId,
            },
          });
        };

        mixpanel.track("submitVoucher", {
          "voucherCode": `${submittedVoucherCode}`,
          "prepayVoucher": `${!!!voucherOnlyValidWithPayment}`
        });
      
      },
      retry: false,
    },
    submittedVoucherCode,
  });

  const handleVoucherSubmit = () => {

    /**
     * Clear any valid voucher code patched to user in the instance where a user
     * wants to change voucher code but has already been to the payment page
     */
    dispatch({
      type: 'UPDATE_USER',
      payload: {
        campaign: '',
        durationInDays: 0,
        durationInMonths: 0,
        organization: '',
        percentageDiscount: 0,
        productId: 0,
        voucherCode: '',
      },
    });
    getDisPlan();
    mixpanel.track("handleVoucherSubmit", {
      "voucherCode": form.voucherCode
    });
  };

  useEffect(() => {
    if (voucher) {
      dispatch({
        type: 'UPDATE_FORM_DATA',
        payload: { voucherCode: voucher },
      })
      getDisPlan();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const createSubmitTrackingEvent = (plan: Plan | undefined, errors: any): TrackingPropertyMap => {
    const event: TrackingPropertyMap = {}
    event[TrackingProperty.TEXT_VARIANT] = state.params.config.toLowerCase() || 'azoomee';
    event[TrackingProperty.LANGUAGE] = state.params.locale.toLowerCase() || 'en';
    if (plan) {
      event[TrackingProperty.CURRENCY] = plan.currency;
      event[TrackingProperty.PLAN_DURATION_MONTHS] = getPlanDuration(plan.productId);
      event[TrackingProperty.PLAN_PRICE] = plan.totalPrice;

      if (plan?.discounts?.length !== 0) {
        event[TrackingProperty.DISCOUNT_PERCENT] = plan.discounts[0].percentage;
        event[TrackingProperty.DISCOUNT_DURATION_MONTHS] = plan.discounts[0].durationInMonths;
      }
    }
    if (form?.voucherCode) {
      event[TrackingProperty.VOUCHER_CODE] = form.voucherCode;
    }
    if (errors) {
      event[TrackingProperty.ERROR_TYPE] = errors;
    }
    return event;
  }

  const sendSubmitTrackingEvent = (plan: Plan | undefined, errors: any) => {
    return mixpanel.track(getEventName(TrackingPage.PLAN_SELECTOR, TrackingAction.CONTINUE_BUTTON_CLICK), createSubmitTrackingEvent(plan, errors));
  }

  const { selectedPlan } = form;

  /**
   * Form submit success handler where we let react-hook-form validate, then we call
   * our state action to save the data in state and persist in localStorage
   */
  const onSubmit = (data: any) => {
    const { selectedPlan } = data;
    clearErrors('voucherCode');

    if (!skipPayment.skip) {
      dispatch({
        type: 'UPDATE_FORM_DATA',
        payload: {
          selectedPlan,
        },
      });
    }
    sendSubmitTrackingEvent(selectedPlan, undefined);
    history.push(ROUTES.USER_REGISTRATION);
  };

  const checkVoucherValidForPayment = (discounts: Discounts) => {
    //Check if we should skip payment or not by checking if onlyValidWithPayment is true and if the percentage is 100 or not
    //If the voucher says payment can be skipped but the percentage is < 100 (indicating payment needs to happen) this catches that 
    //Discounts should never be null here but if for whatever reason it is, default to true so it still goes through payment
    return !!discounts ? (discounts.onlyValidWithPayment || discounts.percentage < 100) : true ;
  }

  const renderPlan = (plan: Plan)=> {
    const {
      billingCycle,
      currency,
      discountPrice,
      productId,
      totalPrice,
    } = plan;

    //don't show the plan if it's a non-payment voucher
    if (checkVoucherValidForPayment(plan.discounts[0]) === false)
    {
      return;
    }
    else
    {
      return <AccordionItem
      data={plan}
      key={productId}
      expanded={plan.productId === form.selectedPlan?.productId}
      mixpanel={mixpanel}
      variant={state.params.config.toLowerCase() || 'azoomee'}
      language={state.params.locale.toLowerCase() || 'en'}
      currency={currency}>
      <AccordionHeader>
        <Text
          as='span'
          color='primary'
          variant='accordionHeader'
          weight='medium'>
          {localeCopy?.plans[billingCycle.toLowerCase()]
            ?.title ||
            fallbackCopy.plans[billingCycle.toLowerCase()]
              .title}
        </Text>
        <div css={{
          [theme.mq.smallMobile]: {
            display: 'inline-grid'
          },
        }}>
          {!!!(plan.discounts.length === 0) && totalPrice && (
            <Text
              as='span'
              color='primary'
              css={{
                marginRight: 4,
                textDecoration: 'line-through',
              }}
              variant='smallPrice'
              weight='medium'>
              {`${pricePresenter(totalPrice, currencySymbols, currency)}`}
            </Text>
          )}
          <Text as='span' variant='price' weight='bold'>
            {`${pricePresenter(
              !!!(plan.discounts.length === 0) ? discountPrice : totalPrice,
              currencySymbols,
              currency
            )}`}
            <Text
              as='span'
              variant='priceFrequency'
              weight='light'>
              {localeCopy?.plans[billingCycle.toLowerCase()]
                ?.priceFrequency ||
                fallbackCopy.plans[billingCycle.toLowerCase()].priceFrequency}
            </Text>
          </Text>
        </div>
      </AccordionHeader>
      <AccordionPanel>
        <div css={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
          marginBottom: 8,
        }}>
          <div css={{
            backgroundColor: theme.colors.accent,
            borderRadius: 6,
            padding: '4px 8px'
          }}>
            <Text color='white'>{(selectedPlan?.discounts && selectedPlan?.discounts?.length !== 0) ?
              ((selectedPlan?.discounts[0]?.percentage < 100) ?
                (textReplacer(
                  localeCopy?.plans[billingCycle.toLowerCase()]?.accentSecondaryMessage ||
                  fallbackCopy.plans[billingCycle.toLowerCase()].accentSecondaryMessage,
                  {
                    '{VOUCHER_PERCENTAGE}': selectedPlan?.discounts[0].percentage,
                    '{VOUCHER_DURATION}': selectedPlan?.discounts[0].durationInMonths,
                    '{MONTH}': selectedPlan?.discounts[0].durationInMonths === 1 ? (
                      localeCopy?.month?.singular || fallbackCopy.month.singular) :
                      (localeCopy?.month?.plural || fallbackCopy.month.plural)
                  })
                ) :
                (
                  (textReplacer(
                    localeCopy?.plans[billingCycle.toLowerCase()]?.accentTertiaryMessage ||
                    fallbackCopy.plans[billingCycle.toLowerCase()].accentTertiaryMessage,
                    {
                      '{VOUCHER_PERCENTAGE}': selectedPlan?.discounts[0].percentage,
                      '{VOUCHER_DURATION}': selectedPlan?.discounts[0].durationInMonths,
                      '{MONTH}': selectedPlan?.discounts[0].durationInMonths === 1 ? (
                        localeCopy?.month?.singular || fallbackCopy.month.singular) :
                        (localeCopy?.month?.plural || fallbackCopy.month.plural)
                    })
                  )
                )
              ) :
              (
                localeCopy?.plans[billingCycle.toLowerCase()]?.accentMessage ||
                fallbackCopy.plans[billingCycle.toLowerCase()].accentMessage
              )
            }</Text>
          </div>
          <div css={{
            paddingLeft: '0.5em'
          }}>
            <Text>{textReplacer(
              localeCopy?.plans[billingCycle.toLowerCase()]?.detailsMessage ||
              fallbackCopy.plans[billingCycle.toLowerCase()].detailsMessage,
              {
                '{PRICE}': pricePresenter(totalPrice, currencySymbols, currency)
              }
            )
            }</Text>
          </div>
        </div>
        {
          (selectedPlan?.discounts && selectedPlan?.discounts?.length !== 0 && selectedPlan?.discounts[0]?.percentage < 100) ?
            <Text css={{
              marginBottom: '0.5em'
            }}>{localeCopy?.plans[billingCycle.toLowerCase()]?.details ||
              fallbackCopy.plans[billingCycle.toLowerCase()].details}</Text>
            :
            undefined
        }
        <Text variant='listItem'>{
          localeCopy?.plans[billingCycle.toLowerCase()]?.additionalInfo ||
          fallbackCopy.plans[billingCycle.toLowerCase()].additionalInfo
        }</Text>
      </AccordionPanel>
    </AccordionItem>
    }
  }

  /**
   * Form submit error handler
   */
  const onError = (errors: any) => {
    console.error('error data: ', errors);
    sendSubmitTrackingEvent(undefined, errors);
  };

  useEffect(() => {
    if (form.selectedPlan === null) {
      setValue('selectedPlan', plans[plans.length - 1], { shouldDirty: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plans])

  const planSelectController = <Controller
    control={control}
    defaultValue={form.selectedPlan || plans[0]}
    name='selectedPlan'
    render={({ onChange }) => (
      <Accordion
        css={{
          marginBottom: 96,
          [theme.mq.desktop]: {
            marginBottom: 48,
          },
        }}
        initialExpanded={plans.findIndex(
          (plan) => plan.productId === form.selectedPlan?.productId
        ) + 1 || 1}
        onChange={onChange}>
        {
          //if we already have a selected plan, render that.
          //otherwise, sort through and individually render all other plans
          !!form.selectedPlan ? renderPlan(form.selectedPlan) : 
          plans
          .filter(plan => !skipPayment.skip)
          //if the user has a voucher code on their account, don't show any plans other than the one selected
          .filter(plan => (!form.voucherCode || (form.voucherCode && plan.discounts.length > 0)))
          //sort by product id in decending order
          .sort((a: any, b: any) => b.productId - a.productId)
          .map((plan: Plan) => {
            return renderPlan(plan)
          })}
      </Accordion>
    )}
    rules={{ required: true }} />;

  return (
    <Page
      isLoading={!fallbackCopy}
      isReady={!!plans && fallbackCopy}
      pageNumber={1}
      configEnablePageCounter={state.config.shouldShowPageCounter}
      title={localeCopy?.title || fallbackCopy?.title}>
      <Form
        disableEnterKeypress={true}
        onSubmit={handleSubmit(onSubmit, onError)}>
        <Fragment>
          {plans && !!plans.length ? planSelectController : null }
          {skipPayment.skip && (
            <VoucherInfo
              header={localeCopy?.components?.basket?.prepayVoucherAppliedHeader ||
                fallbackCopy.components.basket.prepayVoucherAppliedHeader}
              details={textReplacer(
                localeCopy?.components?.basket?.prepayVoucherAppliedDetails ||
                fallbackCopy.components.basket.prepayVoucherAppliedDetails,
                {
                  '{VOUCHER_DURATION}': form.selectedPlan?.discounts[0].durationInMonths,
                  '{MONTH}': form.selectedPlan?.discounts[0].durationInMonths === 1 ? (
                    localeCopy?.month?.singular || fallbackCopy.month.singular) :
                    (localeCopy?.month?.plural || fallbackCopy.month.plural)
                }
              )}
            />
          )}
          <Controller
            control={control}
            defaultValue={voucher || form.voucherCode || ''}
            name='voucherCode'
            render={({ onChange, value, name }) => (
              <Input
                additionalMessage={
                  form.voucherCode && (
                    <Text as='span' color='success' weight='light'>
                      {textReplacer(
                        localeCopy?.voucherInput?.successMessage ||
                        fallbackCopy.voucherInput.successMessage,
                        {
                          $$$: form.voucherCode,
                        }
                      )}
                    </Text>
                  )
                }
                buttonLabel={
                  localeCopy?.voucherInput?.button ||
                  fallbackCopy.voucherInput?.button
                }
                css={{
                  marginBottom: 48,
                  [theme.mq.desktop]: {
                    marginBottom: 88,
                  },
                  button: {
                    minWidth: 80
                  }
                }}
                disabled={!!form.voucherCode}
                disabledSubmit={!value}
                id='voucher-input'
                isBusy={disPlanIsLoading}
                errorMessage={errors && errors.voucherCode?.message}
                name={name}
                onChange={onChange}
                onSubmit={handleVoucherSubmit}
                onKeyUp={(e: any) => {
                  if (e.key === 'Enter') {
                    handleVoucherSubmit();
                  }
                }}
                placeholder={
                  localeCopy?.voucherInput?.placeholder ||
                  fallbackCopy.voucherInput.placeholder
                }
                type='text'
                value={value}
                withButton={true}
              />
            )}
            rules={{
              required: {
                message:
                  localeCopy?.voucherInput?.errorMessage ||
                  fallbackCopy.voucherInput.errorMessage,
                value: false,
              },
            }}
          />
          <Button
            css={{
              marginBottom: 16,
              [theme.mq.desktop]: {
                marginBottom: 24,
              },
            }}
            isBusy={plansQuery.isLoading || disPlanIsLoading}
            isBusyText={
              localeCopy?.components?.button?.loading ||
              fallbackCopy?.components.button.loading
            }
            type='submit'
            variant='primary'
            onKeyUp={(e: any) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit(onSubmit, onError).apply(this);
              }
            }}>
            {localeCopy?.submitButton || fallbackCopy?.submitButton}
          </Button>
        </Fragment>
      </Form>
      <Text
        align='center'
        as='p'
        color='muted'
        variant='disclaimer'
        weight='light'
        withMarkdown={true}>
        {localeCopy?.disclaimer || fallbackCopy?.disclaimer}
      </Text>
    </Page>
  );
};

export default PlanSelector;
